import { useCallback, useRef } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { ShiftSiblingData } from '@lp/features/Scheduler/types';
import { ShiftButton, UnblockShiftPopover } from '@lp/features/Scheduler/components';
import { SShiftCellDropdown } from '@lp/features/Scheduler/SchedulerSurgeons/components';
import {
  getSShiftPrimaryText,
  getSShiftSecondaryText,
  getSShiftTypeColors,
  getSShowHrIcon,
} from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import { SShift, SShiftType, ShiftFixationType } from '@/api/apis/resources/schedule';
import { NAShiftButton } from './components';

type SShiftCellProps = {
  shiftData: SShift;
  siblingData?: ShiftSiblingData;
  onShiftChange: (shiftType?: SShiftType) => void;
  lighten?: boolean;
  readonly?: boolean;
  canReset?: boolean;
  canUnblock?: boolean;
  onUnblock?: () => void;
};

export const SShiftCell = ({
  onShiftChange,
  shiftData,
  siblingData,
  lighten,
  readonly,
  canReset,
  canUnblock,
  onUnblock,
}: SShiftCellProps) => {
  const elRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { shiftType, isEditable, fixationType } = shiftData;
  const { isExternal } = siblingData ?? {};
  const isBlockedRequired = !isEditable && fixationType === ShiftFixationType.Required;
  const disabled = !isEditable || readonly;
  const showContent = !siblingData?.hasPrev;
  const primaryText = siblingData?.isExternal ? siblingData.name : getSShiftPrimaryText(shiftData);
  const secondaryText = siblingData?.isExternal ? null : getSShiftSecondaryText(shiftData);
  const colors = getSShiftTypeColors(shiftData, isExternal);
  const isNA = !isExternal && shiftType === SShiftType.OnCall && !!shiftData.unavailabilities?.length;

  const canShowUnblock = isBlockedRequired && canUnblock && !readonly;

  const onClick = useCallback(() => {
    toggleIsOpen();
  }, [toggleIsOpen]);

  return (
    <>
      {isNA ? (
        <NAShiftButton
          ref={elRef}
          shiftData={shiftData}
          lighten={lighten}
          disabled={disabled && !canShowUnblock}
          onClick={onClick}
        />
      ) : (
        <ShiftButton
          ref={elRef}
          lighten={lighten}
          disabled={disabled && !canShowUnblock}
          siblingData={siblingData}
          className={cn(colors.bg, colors.text, colors.border)}
          onClick={onClick}
          showHrIcon={getSShowHrIcon(shiftData)}
        >
          {showContent && (
            <div className="flex flex-col items-start z-1">
              <div className="flex items-center gap-1">{primaryText}</div>
              {secondaryText && <span className={cn('text-xs-r mt-0.5', colors.secondaryText)}>{secondaryText}</span>}
            </div>
          )}
        </ShiftButton>
      )}

      {isOpen && canShowUnblock && onUnblock && (
        <UnblockShiftPopover triggerRef={elRef} isOpen={isOpen} onOpenChange={toggleIsOpen} onUnblock={onUnblock} />
      )}

      {isOpen && !canShowUnblock && (
        <SShiftCellDropdown
          triggerRef={elRef}
          isOpen={isOpen}
          onOpenChange={toggleIsOpen}
          shiftData={shiftData}
          onShiftChange={onShiftChange}
          isExternalRegion={isExternal}
          canReset={canReset}
        />
      )}
    </>
  );
};
